/**
 * Thin client for the FastAPI backend.
 *
 * Every request is sent with `X-User-Email` / `X-User-Name` headers
 * sourced from the MSAL singleton (`getActiveIdentity()`). The /config
 * admin endpoints additionally carry an `X-Config-Key` header taken
 * from `getActiveConfigKey()` — held in memory only and prompted for
 * by the ConfigPage component when the user opens it.
 */
import { getActiveIdentity } from './auth/AuthContext';
import { getActiveConfigKey } from './state/configKey';
import type {
  AppConfig,
  FormView,
  Gap,
  GapCreateInput,
  GapPatchInput,
  KbDrawerPayload,
  Review,
  ReviewCreateInput,
  StreamFormRow,
  StreamSummary,
  SyncJobHistoryRow,
  SyncStatus,
} from './types';

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown) {
    super(typeof detail === 'string' ? detail : JSON.stringify(detail));
    this.status = status;
    this.detail = detail;
  }
}

interface RequestOptions extends RequestInit {
  /** Add `X-Config-Key` from the in-memory store. */
  withConfigKey?: boolean;
}

async function request<T>(path: string, init?: RequestOptions): Promise<T> {
  const identity = getActiveIdentity();
  const ssoHeaders: Record<string, string> = identity
    ? {
        'X-User-Email': identity.email,
        'X-User-Name': identity.name,
      }
    : {};
  if (init?.withConfigKey) {
    const key = getActiveConfigKey();
    if (key) ssoHeaders['X-Config-Key'] = key;
  }

  const r = await fetch(path, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...ssoHeaders,
      ...(init?.headers ?? {}),
    },
  });
  if (r.status === 204) return null as T;
  if (!r.ok) {
    let detail: unknown = await r.text();
    try {
      detail = JSON.parse(detail as string);
    } catch {
      /* keep as text */
    }
    throw new ApiError(r.status, detail);
  }
  const text = await r.text();
  return (text ? JSON.parse(text) : null) as T;
}

// ─── Config ─────────────────────────────────────────────────────────────
export const getConfig = () => request<AppConfig>('/api/config');

// ─── Sync / mode ────────────────────────────────────────────────────────
export const getSyncStatus = () => request<SyncStatus>('/api/sync/status');

export const startSync = (forceFull = false) =>
  request<{ job_id: string; status: string; started_at: string; force_full?: boolean }>(
    '/api/sync',
    {
      method: 'POST',
      body: JSON.stringify({ force_full: forceFull }),
    },
  );

export const setDataMode = (mode: 'online' | 'offline') =>
  request<{ mode: string }>('/api/sync/mode', {
    method: 'POST',
    body: JSON.stringify({ mode }),
  });

export const syncEventsUrl = (jobId: string) => `/api/sync/events/${jobId}`;

// Admin sync-history (spec §14 Phase 4 / §6.2 sync_jobs)
export const listSyncJobs = (limit = 50) =>
  request<SyncJobHistoryRow[]>(`/api/sync/jobs?limit=${limit}`);

// ─── KB content ─────────────────────────────────────────────────────────
// Slim payload for the drawer (spec §8.3).
export const getKbDrawer = (kbNumber: string) =>
  request<KbDrawerPayload>(`/api/kb/${encodeURIComponent(kbNumber)}`);

// ─── Structured form view (spec §8.2) ───────────────────────────────────
export const getForm = (identifier: string, geography?: string | null) => {
  const qs = geography ? `?geography=${encodeURIComponent(geography)}` : '';
  return request<FormView>(`/api/forms/${identifier}${qs}`);
};

// ─── Snapshot-driven streams (spec §8.2) ───────────────────────────────
export const listStreams = (includeOutOfScope: boolean = false) =>
  request<StreamSummary[]>(
    `/api/streams${includeOutOfScope ? '?include_out_of_scope=true' : ''}`,
  );

export type StreamFormsFilters = {
  core_only?: boolean;
  geography?: string;
  unreviewed_only?: boolean;
};

export const listStreamForms = (
  stream: string,
  filters: StreamFormsFilters = {},
) => {
  const params: Record<string, string | undefined> = {};
  if (filters.core_only !== undefined) {
    params.core_only = filters.core_only ? 'true' : 'false';
  }
  if (filters.geography) params.geography = filters.geography;
  if (filters.unreviewed_only) params.unreviewed_only = 'true';
  return request<StreamFormRow[]>(
    `/api/streams/${encodeURIComponent(stream)}/forms${toQuery(params)}`,
  );
};

// ─── Gaps ───────────────────────────────────────────────────────────────
export type GapFilters = {
  stream?: string;
  kb_number?: string;
  form_sys_id?: string;
  geography?: string;
  scope?: 'form' | 'stream';
};

export const listGaps = (filters: GapFilters = {}) => {
  const qs = toQuery(filters);
  return request<Gap[]>(`/api/gaps${qs}`);
};

export const createGap = (payload: GapCreateInput) =>
  request<Gap>('/api/gaps', { method: 'POST', body: JSON.stringify(payload) });

export const patchGap = (id: string, patch: GapPatchInput) =>
  request<Gap>(`/api/gaps/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

export const deleteGap = (id: string) =>
  request<{ deleted: string }>(`/api/gaps/${id}`, { method: 'DELETE' });

export const exportGapsUrl = (filters: GapFilters = {}) =>
  `/api/gaps/export${toQuery(filters)}`;

// ─── Reviews ────────────────────────────────────────────────────────────
export type ReviewFilters = {
  kb_number?: string;
  stream?: string;
  geography?: string;
};

export const listReviews = (filters: ReviewFilters = {}) => {
  const qs = toQuery(filters);
  return request<Review[]>(`/api/reviews${qs}`);
};

export const createReview = (payload: ReviewCreateInput) =>
  request<{ geography: string; written: Review[] }>('/api/reviews', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const deleteReview = (kbNumber: string, geography: string) =>
  request<{ deleted: { kb_number: string; geography: string } }>(
    `/api/reviews/${encodeURIComponent(kbNumber)}/${encodeURIComponent(geography)}`,
    { method: 'DELETE' },
  );

// ─── /config admin ──────────────────────────────────────────────────────

export interface AdminUser {
  id: number;
  email: string;
  full_name: string;
  role: 'lpo' | 'admin' | 'superuser';
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
}

export interface AdminGeography {
  code: string;
  name: string;
  countries: string;
  sort_order: number;
}

export interface AdminSettingsPayload {
  values: Record<
    string,
    { value: string; source: 'override' | 'env' | 'default'; env_var: string; updated_at: string | null; updated_by: string | null }
  >;
  password_status: string;
}

export const adminUnlock = () =>
  request<{ ok: true; user: AdminUser }>('/api/config-admin/unlock', {
    withConfigKey: true,
  });

export const adminListUsers = () =>
  request<AdminUser[]>('/api/config-admin/users', { withConfigKey: true });

export const adminCreateUser = (payload: {
  email: string;
  full_name: string;
  role: 'lpo' | 'admin' | 'superuser';
}) =>
  request<AdminUser>('/api/config-admin/users', {
    method: 'POST',
    body: JSON.stringify(payload),
    withConfigKey: true,
  });

export const adminUpdateUser = (id: number, payload: { role: AdminUser['role']; is_active: boolean }) =>
  request<AdminUser>(`/api/config-admin/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
    withConfigKey: true,
  });

export const adminDeleteUser = (id: number) =>
  request<null>(`/api/config-admin/users/${id}`, {
    method: 'DELETE',
    withConfigKey: true,
  });

export const adminListGeographies = () =>
  request<AdminGeography[]>('/api/config-admin/geographies', { withConfigKey: true });

export const adminSaveGeography = (payload: AdminGeography) =>
  request<AdminGeography>('/api/config-admin/geographies', {
    method: 'POST',
    body: JSON.stringify(payload),
    withConfigKey: true,
  });

export const adminDeleteGeography = (code: string) =>
  request<null>(`/api/config-admin/geographies/${encodeURIComponent(code)}`, {
    method: 'DELETE',
    withConfigKey: true,
  });

export const adminTriggerSync = (forceFull = false) =>
  request<{ job_id: string; status: string; started_at: string; already_running: boolean }>(
    '/api/config-admin/sync',
    {
      method: 'POST',
      body: JSON.stringify({ force_full: forceFull }),
      withConfigKey: true,
    },
  );

export const adminGetSettings = () =>
  request<AdminSettingsPayload>('/api/config-admin/settings', { withConfigKey: true });

export const adminUpdateSettings = (payload: Partial<Record<'servicenow_instance_url' | 'servicenow_test_url' | 'servicenow_username', string>>) =>
  request<AdminSettingsPayload>('/api/config-admin/settings', {
    method: 'PUT',
    body: JSON.stringify(payload),
    withConfigKey: true,
  });

// ─── helpers ────────────────────────────────────────────────────────────
function toQuery(obj: Record<string, string | undefined>): string {
  const entries = Object.entries(obj).filter(
    ([, v]) => v !== undefined && v !== '',
  ) as [string, string][];
  if (entries.length === 0) return '';
  const usp = new URLSearchParams();
  for (const [k, v] of entries) usp.set(k, v);
  return `?${usp.toString()}`;
}
